import React, { useEffect, useState } from 'react';

const PrivacyPolicy = () => {
  const [activeId, setActiveId] = useState('');

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px 0px -70% 0px', // Triggers when section is near top
      threshold: 0,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveId(entry.target.id);
        }
      });
    }, observerOptions);

    const sections = document.querySelectorAll('section[id]');
    sections.forEach((section) => observer.observe(section));

    return () => {
      sections.forEach((section) => observer.unobserve(section));
    };
  }, []);

  // Helper to handle smooth scroll click
  const scrollToSection = (e, id) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveId(id); // Manually set active to avoid lag
    }
  };

  const navItems = [
    { id: 'intro', label: 'Introduction' },
    { id: 'section-1', label: '1. Scope & Applicability' },
    { id: 'section-2', label: '2. Information Collection' },
    { id: 'section-3', label: '3. Data Usage' },
    { id: 'section-4', label: '4. Google API Policy' },
    { id: 'section-5', label: '5. Data Sharing' },
    { id: 'section-6', label: '6. Security' },
    { id: 'section-7', label: '7. Data Retention' },
    { id: 'section-8', label: '8. Managing Privacy' },
    { id: 'section-9', label: '9. Region-Specific' },
    { id: 'section-10', label: '10-12. Misc & Support' },
  ];

  const styles = `
    /* General Layout */
    .terms-container {
      display: flex;
      max-width: 1200px;
      margin: 0 auto;
      padding: 40px 20px;
      gap: 60px;
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
      color: #1e293b; /* Slate-800 */
      line-height: 1.6;
    }

    /* Main Content Column */
    .main-content {
      flex: 1;
      max-width: 800px;
    }

    .main-content h1 {
      font-size: 2.5rem;
      font-weight: 700;
      margin-bottom: 0.5rem;
      color: #0f172a; /* Slate-900 */
    }

    .main-content h2 {
      font-size: 1.5rem;
      font-weight: 600;
      margin-top: 2.5rem;
      margin-bottom: 1rem;
      padding-bottom: 0.5rem;
      border-bottom: 1px solid #e2e8f0;
      color: #1e293b;
      scroll-margin-top: 100px; /* For sticky header clearance */
    }

    .main-content h3 {
      font-size: 1.125rem;
      font-weight: 600;
      margin-top: 1.5rem;
      margin-bottom: 0.75rem;
      color: #334155;
    }

    .main-content p {
      margin-bottom: 1.25rem;
      color: #475569;
    }

    .main-content ul {
      list-style-type: disc;
      padding-left: 1.5rem;
      margin-bottom: 1.5rem;
      color: #475569;
    }

    .main-content li {
      margin-bottom: 0.5rem;
    }

    .main-content li ul {
      margin-top: 0.5rem;
      margin-bottom: 0;
    }

    /* Special Warning/Highlight Boxes */
    .highlight-box {
      background-color: #f8fafc;
      border-left: 4px solid #6366f1; /* Indigo-500 */
      padding: 1rem 1.5rem;
      border-radius: 0 0.5rem 0.5rem 0;
      margin: 1.5rem 0;
      font-size: 0.95rem;
      color: #334155;
    }

    .compliance-box {
      background-color: #f0f9ff; /* Sky-50 */
      border: 1px solid #e0f2fe;
      border-radius: 0.5rem;
      padding: 1.5rem;
      margin: 1.5rem 0;
    }

    .contact-box {
      background-color: white;
      border: 1px solid #cbd5e1;
      border-radius: 0.5rem;
      padding: 1.5rem;
      margin-top: 1rem;
    }

    .contact-box p {
      margin-bottom: 0.5rem;
    }

    /* Sidebar Navigation */
    .sidebar {
      width: 280px;
      flex-shrink: 0;
      display: none; /* Hidden on mobile */
    }

    @media (min-width: 1024px) {
      .sidebar {
        display: block;
      }
    }

    .sticky-nav {
      position: sticky;
      top: 20px;
      padding-left: 20px;
      border-left: 1px solid #e2e8f0;
      max-height: calc(100vh - 40px);
      overflow-y: auto;
    }

    .sticky-nav h3 {
      font-size: 0.75rem;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      font-weight: 600;
      color: #94a3b8;
      margin-bottom: 1rem;
    }

    .nav-links {
      list-style: none;
      padding: 0;
      margin: 0;
    }

    .nav-links li {
      margin-bottom: 0.75rem;
    }

    .nav-links a {
      text-decoration: none;
      font-size: 0.875rem;
      color: #64748b;
      transition: all 0.2s ease;
      display: block;
      border-left: 2px solid transparent;
      padding-left: 0;
      margin-left: -21px; /* Aligns border with parent border */
      padding-left: 19px;
    }

    .nav-links a:hover {
      color: #4f46e5; /* Indigo-600 */
    }

    .nav-links a.active {
      color: #4f46e5;
      font-weight: 500;
      border-left-color: #4f46e5;
    }
  `;
  return (
    <>
      <style>{styles}</style>
      <div className="terms-container">
        {/* LEFT SIDE: Content */}
        <main className="main-content">
          <h1>HiCapy Privacy Policy</h1>
          <p><strong>Last Updated:</strong> 10th of December, 2025</p>
          
          <section id="intro">
            <h2>Introduction</h2>
            <p>
              Your privacy is critically important to us. This Privacy Policy ("Policy") applies to the services provided by HiCapy ("we," "us," or "HiCapy"), including our website, automated email assistant, and AI agent services (collectively, the "Services").
            </p>
            <p>
              HiCapy is an AI-powered executive assistant designed to reduce cognitive load by proactively managing your emails, scheduling, and communications. We operate under a core philosophy: <strong>Your data belongs to you.</strong>
            </p>
            <div className="highlight-box">
              <p>
                We do not sell your personal data. We process your email and calendar data solely to provide the assistance you have requested (e.g., drafting replies, checking availability). We do not use your private email content to train generalized AI models for other customers.
              </p>
            </div>
            <p>By using HiCapy, you acknowledge that you have read and understood this Policy.</p>
          </section>

          <section id="section-1">
            <h2>1. Scope and Applicability</h2>
            <p>This Policy applies to:</p>
            <ul>
              <li><strong>Users:</strong> Individuals who register for a HiCapy account.</li>
              <li><strong>Contacts:</strong> External individuals who communicate with Users via email or calendar invites, where HiCapy processes the data to assist the User.</li>
            </ul>
            <p><em>Note: Our Services connect with third-party providers (Google Workspace, Microsoft Outlook). While we protect data once we receive it, your use of those platforms is also governed by their respective policies.</em></p>
          </section>

          <section id="section-2">
            <h2>2. What Information Do We Collect?</h2>
            
            <h3>A. Information You Provide to Us</h3>
            <ul>
              <li><strong>Account Information:</strong> Name, email address, and authentication credentials (via OAuth).</li>
              <li><strong>Communication Data (Sensitive):</strong> To function as your assistant, HiCapy processes:
                <ul>
                  <li><strong>Emails:</strong> Sender details, subject lines, body text, and timestamps to analyze and draft responses.</li>
                  <li><strong>Calendar:</strong> Event titles, times, attendees, and descriptions to manage your schedule.</li>
                </ul>
              </li>
              <li><strong>Context & Memory Data:</strong>
                <ul>
                  <li><strong>Facts & Preferences:</strong> Information you explicitly teach HiCapy (e.g., "I prefer meetings in the afternoon," "David is the CTO") to improve personalization.</li>
                  <li><strong>Feedback:</strong> Corrections you make to HiCapy's drafts, which are used to refine your personal style profile.</li>
                </ul>
              </li>
            </ul>

            <h3>B. Information We Collect Automatically</h3>
            <ul>
              <li><strong>Usage Logs:</strong> Interaction data (e.g., how often you accept vs. edit a draft).</li>
              <li><strong>Device Data:</strong> IP address, browser type, and operating system.</li>
            </ul>
          </section>

          <section id="section-3">
            <h2>3. How Do We Use Your Information?</h2>
            <p>We use your information for the following purposes:</p>
            <ul>
              <li><strong>Proactive Assistance:</strong> To analyze incoming emails, determine if they require action, and generate draft responses or calendar actions.</li>
              <li><strong>Contextual Personalization:</strong> To retrieve relevant facts from your stored "Memory" (e.g., recalling a past conversation) to generate accurate drafts.</li>
              <li><strong>Service Improvement:</strong> To refine the specific style models associated with your account (e.g., learning your tone).</li>
              <li><strong>Security:</strong> To detect abnormal behavior or unauthorized access attempts.</li>
            </ul>
          </section>

          <section id="section-4">
            <h2>4. Google API Services User Data Policy</h2>
            <div className="compliance-box">
              <p>HiCapy's use and transfer to any other app of information received from Google APIs will adhere to the <strong>Google API Services User Data Policy</strong>, including the Limited Use requirements.</p>
              <p>Specifically regarding Gmail and Calendar scopes:</p>
              <ul>
                <li><strong>No Generalized Training:</strong> We do not use data obtained from Google Workspace APIs (email bodies, calendar details) to train generalized artificial intelligence models that are shared with other customers.</li>
                <li><strong>No Human Readability:</strong> We do not allow humans to read your email data unless:
                  <ul>
                    <li>You have explicitly agreed for specific support cases;</li>
                    <li>It is necessary for security purposes (e.g., investigating abuse); or</li>
                    <li>It is required by applicable law.</li>
                  </ul>
                </li>
                <li><strong>No Advertising:</strong> We never use Google User Data for serving advertisements.</li>
              </ul>
            </div>
          </section>

          <section id="section-5">
            <h2>5. Do We Share Your Information?</h2>
            <p>We do not sell your personal information. We share data only in these limited scenarios:</p>
            <ul>
              <li><strong>AI Inference Providers:</strong> We transmit text data to trusted AI providers (e.g., Groq, OpenAI, Anthropic) solely for the purpose of generating responses and analysis. These providers are contractually limited to using the data for inference only and not for training their foundation models on your data.</li>
              <li><strong>Cloud Infrastructure:</strong> We use trusted cloud providers (e.g., AWS, Google Cloud) to host our secure database and backend.</li>
              <li><strong>Legal Requirements:</strong> We may disclose information if required by valid legal process.</li>
            </ul>
          </section>

          <section id="section-6">
            <h2>6. How Do We Secure Your Information?</h2>
            <p>We employ enterprise-grade security measures:</p>
            <ul>
              <li><strong>Encryption:</strong> All data is encrypted in transit (TLS 1.2+) and at rest (AES-256).</li>
              <li><strong>Ephemeral Processing:</strong> Where possible, raw email bodies are processed in memory and not permanently stored, retaining only the necessary metadata and generated vectors.</li>
              <li><strong>OAuth Tokens:</strong> We rely on secure OAuth 2.0 protocols for email access and do not store your password.</li>
            </ul>
          </section>

          <section id="section-7">
            <h2>7. Data Retention</h2>
            <ul>
              <li><strong>Emails:</strong> Raw email content is retained only as long as necessary to generate a response or await your approval.</li>
              <li><strong>Memory (Mem0):</strong> Facts and preferences derived from your interactions are stored indefinitely to provide context, until you explicitly delete them via your dashboard.</li>
              <li><strong>Account Deletion:</strong> If you delete your account, all data (including your personalized memory graph) is permanently removed within 30 days.</li>
            </ul>
          </section>

          <section id="section-8">
            <h2>8. Managing Your Privacy</h2>
            <p>You have the right to:</p>
            <ul>
              <li><strong>Access & Edit Memory:</strong> View and delete specific facts HiCapy has learned about you via the "Memory" tab in your dashboard.</li>
              <li><strong>Revoke Access:</strong> Disconnect Google/Microsoft integrations at any time via account settings.</li>
              <li><strong>Request Deletion:</strong> Request full deletion of your personal data.</li>
            </ul>
          </section>

          <section id="section-9">
            <h2>9. Region-Specific Disclosures</h2>
            
            <h3>A. For Users in India (DPDP Act Compliance)</h3>
            <ul>
              <li><strong>Data Fiduciary:</strong> HiCapy acts as a Data Fiduciary.</li>
              <li><strong>Consent:</strong> By connecting your email account, you signify consent for us to process your communications for the purpose of assistance.</li>
              <li><strong>Grievance Redressal:</strong> Contact our Grievance Officer for concerns.</li>
            </ul>

            <h3>B. For Users in California (CCPA)</h3>
            <p>We do not sell personal information. You have the right to request disclosure and deletion of data.</p>

            <h3>C. For Users in Europe (GDPR)</h3>
            <p>We process data based on Contractual Necessity (to provide the assistant service) and Legitimate Interest. You have the right to object, request portability, and lodge a complaint.</p>
          </section>

          <section id="section-10">
            <h2>10. Children</h2>
            <p>HiCapy is a professional tool intended for adults. We do not knowingly collect data from children under 18.</p>

            <h2>11. Changes to this Policy</h2>
            <p>We may update this Policy periodically. If we make material changes, we will notify you via email or within the app.</p>

            <h2>12. Support</h2>
            <div className="contact-box">
              <p>For legal inquiries, specific support requests, or concerns regarding these terms, please contact:</p>
              <p><strong>Email:</strong> <a href="mailto:legal.hicapy@gmail.com" style={{color: '#4f46e5', textDecoration: 'none'}}>legal.hicapy@gmail.com</a></p>
            </div>
          </section>

        </main>

        {/* RIGHT SIDE: Sidebar Navigation */}
        <aside className="sidebar">
          <div className="sticky-nav">
            <h3>Contents</h3>
            <ul className="nav-links">
              {navItems.map((item) => (
                <li key={item.id}>
                  <a
                    href={`#${item.id}`}
                    className={activeId === item.id ? 'active' : ''}
                    onClick={(e) => scrollToSection(e, item.id)}
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </div>
    </>
  );
};

export default PrivacyPolicy;